import React from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";

import { TEACHING_MATERIAL_KEYWORDS } from "../../../constants/teaching_materials";
import {
  Context,
  fetchSimpleTeachingMaterials,
  notFoundPath,
  fetchTemporaryToken,
  clearTemporaryToken,
  classDataPath,
  API_URL,
  FETCH_TEMPORARY_TOKEN_FAILURE,
} from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import { DownloadAllPortfolio } from "../../../templates/teacher/DownloadAllPortfolio";

export default () => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const params = useParams<{
    class_id: string;
  }>();
  const history = useHistory();
  const { state: locationState } = useLocation<{
    nameClass: string;
  }>();
  const class_id = parseInt(params.class_id);
  const nameClass = locationState?.nameClass;

  const [pendingMaterialId, setPendingMaterialId] = React.useState<
    number | null
  >(null);
  const pendingWindowRef = React.useRef<Window | null>(null);

  const portfolioMaterials = React.useMemo(
    () => state.simple_teaching_materials.filter(m => m.portfolio_type),
    [state.simple_teaching_materials],
  );

  const middleItems = React.useMemo(
    () =>
      portfolioMaterials
        .filter(m => m.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE))
        .map(m => ({
          name: m.name,
          title: m.title,
          materialId: m.id,
          package_version: m.package_version,
        })),
    [portfolioMaterials],
  );

  const highItems = React.useMemo(
    () =>
      portfolioMaterials
        .filter(m => m.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH))
        .map(m => ({
          name: m.name,
          title: m.title,
          materialId: m.id,
          package_version: m.package_version,
        })),
    [portfolioMaterials],
  );

  const otherItems = React.useMemo(
    () =>
      portfolioMaterials
        .filter(
          m =>
            !m.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE) &&
            !m.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH),
        )
        .map(m => ({
          name: m.name,
          title: m.title,
          materialId: m.id,
          package_version: m.package_version,
        })),
    [portfolioMaterials],
  );

  React.useEffect(() => {
    dispatch(peep(fetchSimpleTeachingMaterials(class_id)));
  }, [class_id, peep, dispatch]);

  React.useEffect(() => {
    if (!nameClass) {
      history.replace(notFoundPath);
    }
  }, [history, nameClass]);

  React.useEffect(() => {
    if (state.temporaryToken && pendingMaterialId !== null) {
      if (pendingWindowRef.current) {
        pendingWindowRef.current.location.href = `${API_URL}/teaching_materials/${pendingMaterialId}/download_all_student_portfolio_pdf?class_id=${class_id}&temporary_token=${state.temporaryToken}`;
        pendingWindowRef.current = null;
      }
      dispatch(clearTemporaryToken());
      setPendingMaterialId(null);
    }
  }, [state.temporaryToken, pendingMaterialId, class_id, dispatch]);

  React.useEffect(() => {
    if (
      state.lastAction === FETCH_TEMPORARY_TOKEN_FAILURE &&
      pendingWindowRef.current
    ) {
      pendingWindowRef.current.close();
      pendingWindowRef.current = null;
      setPendingMaterialId(null);
    }
  }, [state.lastAction]);

  const goBack = React.useCallback(
    () =>
      history.push(`${classDataPath}/${class_id}`, {
        nameClass,
      }),
    [class_id, history, nameClass],
  );

  const handleDownloadMaterial = React.useCallback(
    (materialId: number) => {
      pendingWindowRef.current = window.open("", "_blank");
      setPendingMaterialId(materialId);
      dispatch(peep(fetchTemporaryToken()));
    },
    [dispatch, peep],
  );

  return nameClass ? (
    <DownloadAllPortfolio
      calling={calling}
      middleItems={middleItems}
      highItems={highItems}
      otherItems={otherItems}
      goBack={goBack}
      onDownloadMaterial={handleDownloadMaterial}
    />
  ) : null;
};
